<template>
  <div class="h-full flex flex-col">
    <div class="w-full h-full bg-white px-5 rounded-[12px] py-4 space-y-5 border border-grayLight flex flex-col">
      <div class="flex items-center">
        <p class="font-semibold text-bodyText text-[14px]">Projects</p>
        <router-link :to="{ name: 'Projects' }" class="ml-1">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.25 10.5L8.75 7L5.25 3.5" stroke="#9CA1A3" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </router-link>
      </div>

      <div class="space-y-4">
        <template v-if="loading">
          <SkeletonLoader v-for="n in 2" :key="n" width="100%" height="160px" />
        </template>

        <template v-else-if="activeProjects.length > 0">
          <div
            v-for="project in activeProjects"
            :key="project.project_id"
            @click="goToProject(project.project_id)"
            class="w-full border border-grayLight rounded-lg py-4 px-5 space-y-4 flex flex-col cursor-pointer hover:shadow-sm transition-shadow"
            style="background: linear-gradient(180deg, #FFFFFF 0%, #F3F4F6 100%);"
          >
            <!-- Name + status badge -->
            <div class="flex justify-between w-full items-center">
              <h2 class="text-[16px] text-bodyText font-semibold">{{ project.project_name }}</h2>
              <div class="flex py-[4px] px-3 rounded-full space-x-1.5 items-center" style="background-color: rgba(35, 214, 149, 0.1); border: 1px solid #23D695;">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.00098 1.83301C11.398 1.83319 14.167 4.60292 14.167 8C14.1668 11.3969 11.3979 14.1658 8.00098 14.166C4.60389 14.166 1.83416 11.397 1.83398 8C1.83398 4.60281 4.60379 1.83301 8.00098 1.83301ZM7.75391 4.0127C7.20457 4.01274 6.75408 4.46339 6.75391 5.0127V7.74609C6.75391 8.46685 7.16908 9.24697 7.76855 9.66895L7.89062 9.74902L9.95801 10.9824V10.9805C10.1286 11.088 10.3077 11.126 10.4678 11.126C10.8077 11.1258 11.1437 10.9537 11.332 10.6309C11.6185 10.151 11.4553 9.53888 10.9785 9.26074L8.91699 8.03027L8.91211 8.02734L8.88379 7.99902C8.86169 7.97418 8.83697 7.93999 8.81445 7.90039C8.79206 7.86097 8.77505 7.82253 8.76465 7.79004C8.75372 7.75585 8.7539 7.74127 8.75391 7.74609V5.0127C8.75374 4.46336 8.30328 4.0127 7.75391 4.0127Z" fill="#23D695" stroke="#23D695"/>
                </svg>
                <p class="text-[12px] font-medium" style="color: #23D695;">
                  {{ project.status  }}
                </p>
              </div>
            </div>

            <!-- Progress bar -->
            <div class="flex items-center w-full gap-3">
              <div class="relative flex-1 h-[6px] bg-[#F3F4F6] rounded-full overflow-hidden">
                <div
                  class="absolute top-0 left-0 h-full bg-primaryDarkBg rounded-full"
                  :style="{ width: `${project.progress || 0}%` }"
                ></div>
              </div>
              <span class="text-[12px] font-medium text-bodyText whitespace-nowrap">{{ project.progress || 0 }}%</span>
            </div>

            <!-- Avatars + View project -->
            <div class="flex justify-between w-full items-center">
              <div class="flex items-center">
                <div class="flex -space-x-2">
                  <div
                    v-for="(member, i) in (project.members || []).slice(0, 4)"
                    :key="i"
                    class="relative group/avatar"
                  >
                    <img
                      :src="member.profile_picture || require('@/assets/users/default-profile-picture.jpg')"
                      :alt="member.name"
                      class="h-7 w-7 rounded-full border-2 border-white object-cover"
                    />
                    <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-800 text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover/avatar:opacity-100 transition-opacity pointer-events-none z-10">
                      {{ member.name }}
                      <div class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
                <span
                  v-if="project.member_count > 4"
                  class="ml-1 text-[12px] text-grayDark"
                >+{{ project.member_count - 4 }}</span>
              </div>
              <button
                class="text-[13px] py-1.5 px-4 rounded-[6px] font-medium"
                style="background-color: rgba(204, 204, 204, 0.04); color: #222222; box-shadow: 0 1px 4px 0 rgba(0,0,0,0.10);"
              >
                View project
              </button>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="w-full border border-grayLight rounded-lg py-10 px-6 flex justify-center items-center">
            <p class="text-[14px] text-grayDark">No active projects yet.</p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/axios';
import SkeletonLoader from '@/components/SkeletonLoader.vue';

export default {
  name: 'OverviewProjectsComponent',
  components: {
    SkeletonLoader,
  },
  data() {
    return {
      projects: [],
      loading: true,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },

    activeProjects() {
      return this.projects
        .filter(p => {
          const s = (p.project_status || p.status || '').toLowerCase();
          return s === 'active' || s === 'processing';
        })
        .slice(0, 2);
    },
  },
  methods: {
    async fetchProjects() {
      this.loading = true;
      try {
        const response = await apiClient.get('/web/projects_all', {
          params: { active_profile: 'manager', page_size: 2 },
        });
        this.projects = response.data.data || [];
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        this.loading = false;
      }
    },

    goToProject(projectId) {
      this.$router.push({ name: 'ProjectDetails', params: { projectId } });
    },
  },
  created() {
    this.fetchProjects();
  },
};
</script>
